// The control row of a column on a phone: sort, direction, count and "...".
// Everything else a column header offers on a desktop (category, card size,
// card body, filters, reset, share, the how-it-works panel) is in the options
// sheet behind "...". Same props as ColumnHeader, so BoardColumn swaps one for
// the other.
//
// No tooltips and no hover panels here: neither can be reached by touch.

import { useMemo, useState } from 'react';
import { Box, ButtonBase, Menu, MenuItem } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { getMetric } from '../api/metrics';
import type { ColumnHeaderProps } from '../board/ColumnHeader';
import { HIDDEN_SORT_KEYS } from '../board/sortKeys';
import { numSx } from '../../../../components/common/page/numberStyles';
import { ColumnOptionsSheet } from './ColumnOptionsSheet';
import {
    ArrowDownIcon,
    ArrowUpIcon,
    ChevronDownIcon,
    MoreDotsIcon,
    RetryIcon,
} from './icons';
import { ink, surface } from './surface';

const TAP = 44;

const iconButtonSx = (theme: Theme) => ({
    width: TAP,
    height: TAP,
    flexShrink: 0,
    color: ink(theme, 0.7),
    borderRadius: '50%',
});

function formatErrorTime(at: number): string {
    if (!at) return '';
    return new Date(at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

export function PhoneColumnHeader({
    config,
    schema,
    filters,
    sortKeys,
    bodyMode,
    cardView,
    eventCount,
    pendingCount,
    isFetching,
    filtersDirty,
    lastError,
    lastErrorAt,
    now,
    onFiltersChange,
    onReset,
    onShare,
    onBodyModeChange,
    onCardViewChange,
    onApplyPending,
    onRetry,
    onFocusEvent,
    onCategoryChange,
}: ColumnHeaderProps) {
    const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    // Same list as the desktop select: a key the column hides is only shown
    // while it is the one in use.
    const shownSortKeys = useMemo(
        () =>
            sortKeys.filter(
                k => !HIDDEN_SORT_KEYS.has(k) || k === filters.sortBy
            ),
        [sortKeys, filters.sortBy]
    );
    const sortOffered = sortKeys.includes(filters.sortBy);
    const sortLabel = sortOffered ? getMetric(filters.sortBy).label : 'Sort by';
    const descending = filters.sortDir === 'desc';

    const errorTime = formatErrorTime(lastErrorAt);
    const errorTitle = errorTime
        ? `Last update failed · ${errorTime}`
        : 'Last update failed';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                py: 1,
                pl: 2,
                pr: 1,
                flexShrink: 0,
            }}
        >
            <ButtonBase
                // The visible text is the key in use, so the name starts with
                // "Sort by" and contains it; with no key in use the visible text
                // already is "Sort by".
                aria-label={sortOffered ? `Sort by ${sortLabel}` : sortLabel}
                aria-haspopup="menu"
                aria-expanded={Boolean(sortAnchor)}
                onClick={event => setSortAnchor(event.currentTarget)}
                sx={theme => ({
                    height: TAP,
                    minWidth: 0,
                    flex: '0 1 auto',
                    gap: '4px',
                    pl: '12px',
                    pr: '8px',
                    border: `1px solid ${ink(theme, 0.25)}`,
                    borderRadius: '8px',
                    bgcolor: surface(theme),
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                })}
            >
                <Box
                    component="span"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {sortLabel}
                </Box>
                <ChevronDownIcon />
            </ButtonBase>
            <Menu
                open={Boolean(sortAnchor)}
                anchorEl={sortAnchor}
                onClose={() => setSortAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {shownSortKeys.map(key => (
                    <MenuItem
                        key={key}
                        role="menuitemradio"
                        aria-checked={key === filters.sortBy}
                        selected={key === filters.sortBy}
                        onClick={() => {
                            setSortAnchor(null);
                            onFiltersChange({ sortBy: key });
                        }}
                        sx={{ minHeight: TAP, fontSize: 15 }}
                    >
                        {getMetric(key).label}
                    </MenuItem>
                ))}
            </Menu>

            <ButtonBase
                // The order it shows now, and what a tap does about it.
                aria-label={
                    descending
                        ? 'Highest first, switch to lowest first'
                        : 'Lowest first, switch to highest first'
                }
                onClick={() =>
                    onFiltersChange({ sortDir: descending ? 'asc' : 'desc' })
                }
                sx={iconButtonSx}
            >
                {descending ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </ButtonBase>

            <Box
                component="span"
                sx={theme => ({
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'right',
                    fontSize: 13,
                    lineHeight: 'normal',
                    color: ink(theme, 0.6),
                    whiteSpace: 'nowrap',
                    ...numSx,
                })}
            >
                {eventCount > 0
                    ? `${eventCount} ${eventCount === 1 ? 'event' : 'events'}`
                    : ''}
            </Box>

            {pendingCount > 0 && (
                <ButtonBase
                    // The text says "N new"; the name keeps those words and says
                    // what a tap does and where.
                    aria-label={`Show ${pendingCount} new ${
                        pendingCount === 1 ? 'event' : 'events'
                    } in ${config.title}`}
                    onClick={onApplyPending}
                    sx={{ height: TAP, px: 0.5, flexShrink: 0 }}
                >
                    <Box
                        component="span"
                        sx={theme => ({
                            px: 1,
                            py: '3px',
                            borderRadius: '12px',
                            fontSize: 13,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.14),
                        })}
                    >
                        {pendingCount} new
                    </Box>
                </ButtonBase>
            )}

            {isFetching && !lastError && (
                <Box
                    component="span"
                    role="status"
                    aria-label="Updating"
                    sx={theme => ({
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: theme.palette.primary.main,
                        opacity: 0.7,
                        flexShrink: 0,
                    })}
                />
            )}

            {lastError && (
                <ButtonBase
                    aria-label="Reload"
                    title={errorTitle}
                    disabled={isFetching}
                    onClick={onRetry}
                    sx={theme => ({
                        ...iconButtonSx(theme),
                        color: theme.palette.error.main,
                        '@keyframes boardSpin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                        },
                        '& svg': {
                            animation: isFetching
                                ? 'boardSpin 1s linear infinite'
                                : 'none',
                        },
                    })}
                >
                    <RetryIcon />
                </ButtonBase>
            )}

            <ButtonBase
                aria-label="Column options"
                aria-haspopup="dialog"
                onClick={() => setSheetOpen(true)}
                sx={iconButtonSx}
            >
                <MoreDotsIcon />
            </ButtonBase>

            <ColumnOptionsSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                config={config}
                schema={schema}
                filters={filters}
                bodyMode={bodyMode}
                cardView={cardView}
                filtersDirty={filtersDirty}
                now={now}
                onFiltersChange={onFiltersChange}
                onReset={onReset}
                onShare={onShare}
                onBodyModeChange={onBodyModeChange}
                onCardViewChange={onCardViewChange}
                onCategoryChange={onCategoryChange}
                onFocusEvent={onFocusEvent}
            />
        </Box>
    );
}
